/**
 * Which workspace a portal-hosted widget is edited in — the one place that says it
 * (`.docs/46-blog-and-forms.md` §7).
 *
 * A widget's kind may be **hosted by a portal module** (`widget.hosted_by`, snapshotted
 * from its type when it was created): a blog's posts are written in the portal, a forms
 * widget's leads land in it. The generic widget page stays generic — its "no branching
 * on `widget_type`" test stands — and what it gains is a header action to the workspace,
 * chosen here by the server-stated `hosted_by`. The sidebar's row for such a widget links
 * straight to the workspace, and the studio client page's Widgets tab links to the
 * studio's half of it. All three read this table and none of them names a surface.
 *
 * **A `Record` over `HostedSurface`**, so a surface added to `packages/shared` is a type
 * error here until somebody decides where it goes — and a half that has no page is a
 * `null` written down, never an address that 404s. A forms widget's studio half is one:
 * the studio works forms over MCP and a studio-side inbox is *Deferred* (decision 12), so
 * its Widgets tab offers no workspace link for one rather than a link to nothing. Its
 * client half is the inbox, `/app/widgets/$widgetRef/submissions` (`pages/forms/`).
 *
 * Addresses rather than route objects, for the sidebar's reason (`shell/nav.ts`): a nav
 * item is an `href`, and a `Link` takes one as its `to`.
 */

import type { HostedSurface } from '@fareast/shared'

/** One surface's workspace, in both portals. */
export type HostedWorkspace = {
  /** The widget page's header action — "Open the blog". */
  readonly open: string
  /** The client's workspace, for one widget. */
  readonly clientHref: (widgetRef: string) => string
  /**
   * The studio's workspace, for one client's widget — or null for a surface the studio
   * works over MCP and has no page for.
   */
  readonly studioHref: ((slug: string, widgetRef: string) => string) | null
}

export const HOSTED_WORKSPACES: Readonly<Record<HostedSurface, HostedWorkspace | null>> = {
  blog: {
    open: 'Open the blog',
    clientHref: (widgetRef) => `/app/widgets/${encodeURIComponent(widgetRef)}/posts`,
    studioHref: (slug, widgetRef) =>
      `/studio/clients/${encodeURIComponent(slug)}/blog/${encodeURIComponent(widgetRef)}`,
  },
  forms: {
    open: 'Open the inbox',
    clientHref: (widgetRef) => `/app/widgets/${encodeURIComponent(widgetRef)}/submissions`,
    // A studio-side inbox is Deferred (decision 12): the studio works forms over MCP.
    studioHref: null,
  },
}

/** The workspace a widget is edited in, or null for one that runs as its own service. */
export function hostedWorkspaceOf(hostedBy: HostedSurface | null): HostedWorkspace | null {
  return hostedBy === null ? null : HOSTED_WORKSPACES[hostedBy]
}
